// flow/hooks/useFlowProjectSettings.ts
import { useState, useCallback, useRef, useEffect } from 'react'
import { useGetProjectAccessRules, useGetProject } from '@/stores/projectsStore'

interface UseFlowProjectSettingsProps {
    projectId: string
    showProjectSettings: boolean
}

export function useFlowProjectSettings({ projectId, showProjectSettings }: UseFlowProjectSettingsProps) {
    const [projectSettingsAccessRules, setProjectSettingsAccessRules] = useState<any[]>([])
    const [projectSettings, setProjectSettings] = useState<any>(null)
    const [projectInfo, setProjectInfo] = useState({
        projectName: '',
        projectDescription: '',
        projectType: '',
        organizationName: '',
    })

    // Hooks for API calls
    const getProjectAccessRules = useGetProjectAccessRules()
    const getProject = useGetProject()

    // Fetch project settings and access rules
    const fetchProjectSettings = useCallback(() => {
        if (!projectId) return

        getProject.execute({
            id: projectId,
            onSuccess: (response) => {
                // Try to extract settings from different possible locations
                let settings = response.data?.settings
                if (!settings && response.data?.project?.settings) {
                    settings = response.data.project.settings
                } if (Array.isArray(settings)) {
                    settings = settings[0] || null
                }

                // Update projectInfo with all relevant data from backend
                const projectName =
                    response.data?.projectName ||
                    response.data?.project?.projectName ||
                    ''
                const projectDescription =
                    response.data?.projectDescription ||
                    response.data?.project?.projectDescription ||
                    ''
                const projectType =
                    response.data?.projectType ||
                    response.data?.project?.projectType ||
                    ''
                const organizationName =
                    response.data?.organization?.name ||
                    response.data?.project?.organization?.name ||
                    ''

                setProjectInfo({
                    projectName,
                    projectDescription,
                    projectType,
                    organizationName,
                })

                // Always set systemAgentEnabled on settings object
                const systemAgentEnabled =
                    typeof response.data?.systemAgentEnabled !== 'undefined'
                        ? response.data.systemAgentEnabled
                        : typeof response.data?.project?.systemAgentEnabled !== 'undefined'
                            ? response.data.project.systemAgentEnabled
                            : null

                if (settings && typeof settings === 'object') {
                    settings.systemAgentEnabled = systemAgentEnabled
                } if (!settings || typeof settings !== 'object') {
                    setProjectSettings(undefined)
                } else {
                    setProjectSettings(settings)
                }
            },
            onError: (error) => {
                setProjectSettings(undefined)
                setProjectInfo({ projectName: '', projectDescription: '', projectType: '', organizationName: '' })
                console.error('Error fetching project settings:', error)
            },
        })

        // Fetch access rules
        getProjectAccessRules.execute({
            id: projectId,
            onSuccess: (response) => {
                setProjectSettingsAccessRules(response.data?.accessRules || [])
            },
            onError: (error) => {
                setProjectSettingsAccessRules([])
                console.error('Error fetching access rules:', error)
            },
        })
    }, [projectId]) // Only depend on projectId

    // Track if we've fetched settings to prevent repeated fetches
    const hasFetchedProjectSettings = useRef(false)

    useEffect(() => {
        if (showProjectSettings && !hasFetchedProjectSettings.current) {
            hasFetchedProjectSettings.current = true
            fetchProjectSettings()
        }
        if (!showProjectSettings) {
            hasFetchedProjectSettings.current = false
        }
    }, [showProjectSettings, fetchProjectSettings])

    return {
        projectSettings,
        projectSettingsAccessRules,
        projectInfo,
        setProjectInfo,
        fetchProjectSettings,
    }
}
